import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { ContactLanguage } from '~/api/clients/types';

import { useProducts } from '~/api/products';
import { useUser } from '~/api/user';
import { Button, Dialog, useTable } from '~/components';
import { Field, Select } from '~/components/Form';
import { bypassBlocker } from '~/constants/router';
import { ColumnIds } from '~/constants/table';
import { SearchParamKeys, SearchParamModals } from '~/constants/url';
import { useProductCategories } from '~/hooks/useProductCategories';
import { routes } from '~/providers/RouterProvider/router.routes';
import { filterBoolean } from '~/utils/arrays';
import { persistFormDataInSession } from '~/utils/form';
import { qs } from '~/utils/searchParams';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useSelectedContactLanguage } from '../../FormLines.hooks';
import { createLineFromProduct } from '../../FormLines.utils';
import { useSectionContext } from '../../Section/Section.context';
import { bodyStyle, dialogStyle, searchStyle, toolbarStyle } from './ProductPickerDialog.css';
import { isProductSelectable } from './ProductPickerDialog.utils';
import { useProductsTableColumns } from './useProductsTableColumns';

type Props = {
  onClose: () => void;
};

export const ProductPickerDialog = ({ onClose }: Props) => {
  return (
    <Dialog.Body className={dialogStyle}>
      <ProductPickerDialogContent onClose={onClose} />
    </Dialog.Body>
  );
};

const ProductPickerDialogContent = ({ onClose }: Props) => {
  const { data: user } = useUser();
  const { data: products } = useProducts();

  const navigate = useNavigate();
  const location = useLocation();

  const {
    clearErrors,
    formState: { isSubmitted },
    getValues,
  } = useFormWithLinesContext();
  const {
    linesFieldArray: { append },
  } = useSectionContext();

  const contactLanguage = useSelectedContactLanguage();
  const [language, setLanguage] = useState<ContactLanguage>(contactLanguage);

  const { t } = useTranslation(['common', 'products']);

  const data = useMemo(
    () =>
      [...products].sort((a, b) => {
        if (a.status === b.status) return (a.name[user.lang] ?? '').localeCompare(b.name[user.lang] ?? '');

        const priority = { Approved: 2, InReview: 3, Rejected: 1 } as const;
        return priority[a.status] > priority[b.status] ? -1 : 1;
      }),
    [products, user.lang],
  );

  const columns = useProductsTableColumns(language);
  const productCategories = useProductCategories();

  const [table, Table] = useTable({
    columns,
    data,
    enableRowSelection: ({ original: product }) => isProductSelectable({ status: product.status }),
    initialState: {
      columnVisibility: {
        [ColumnIds.productsCategories]: productCategories.length > 0,
      },
    },
  });

  const selectedProducts = Object.keys(table.state.rowSelection)
    .map((id) => products.find((product) => product.id === +id))
    .filter(filterBoolean);

  const languageItems = (['nl', 'fr', 'en'] satisfies ContactLanguage[]).map((languageOption) => ({
    label: t(`common:languagesShort.${languageOption}`),
    value: languageOption,
  }));

  const confirm = () => {
    selectedProducts.forEach((product) => append(createLineFromProduct(product)));

    // Clear the sections error as appending a line doesn't retrigger the validation.
    // As a side effect, this clears all errors of each line as well, which is acceptable behavior.
    if (isSubmitted) clearErrors('sections');

    onClose();
  };

  const navigateToCreateProduct = () => {
    persistFormDataInSession(window.location.pathname, getValues());

    navigate(
      {
        pathname: routes.createProduct,
        search: qs({
          [SearchParamKeys.REDIRECT_PATH]: location.pathname,
          [SearchParamKeys.REDIRECT_MODAL]: SearchParamModals.ADD_PRODUCT,
        }),
      },
      { state: bypassBlocker },
    );
  };

  return (
    <>
      <Dialog.Header title={t('products:modal.title')}>
        <Button emphasis="secondary" icon="Add" onClick={navigateToCreateProduct}>
          {t('products:modal.createNew')}
        </Button>
      </Dialog.Header>

      <div className={bodyStyle}>
        <Table.Root table={table}>
          <Table.Menu>
            <div className={toolbarStyle}>
              <div className={searchStyle}>
                <Table.Search />
              </div>

              <Field.Root>
                <Select.Root items={languageItems} onValueChange={(next) => next && setLanguage(next)} value={language}>
                  <Select.Button aria-label={t('common:languageSelector', { value: t(`common:languages.${language}`) })} />
                  <Select.List>
                    {languageItems.map((item) => (
                      <Select.Item aria-label={t(`common:languages.${item.value}`)} key={item.value} value={item.value}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.List>
                </Select.Root>
              </Field.Root>

              <Table.Filters>
                <Table.Filter columnId={ColumnIds.productsStatus} type="multiselect" />
                <Table.Filter columnId={ColumnIds.productsActivityType} type="multiselect" />
                <Table.Filter columnId={ColumnIds.productsCategories} hideWhen="noOptions" type="multiselect" />
              </Table.Filters>
            </div>
          </Table.Menu>

          <Table.Grid>
            <Table.Header />
            <Table.Body>
              {(row) => <Table.Row onClick={row.getCanSelect() ? () => row.toggleSelected() : undefined} row={row} />}
            </Table.Body>
          </Table.Grid>

          <Table.Pagination />
        </Table.Root>
      </div>

      <Dialog.Actions>
        <Dialog.Close render={<Button emphasis="secondary">{t('common:actions.cancel')}</Button>} />

        <Button disabled={selectedProducts.length === 0} onClick={confirm}>
          {t('products:modal.add', { count: selectedProducts.length })}
        </Button>
      </Dialog.Actions>
    </>
  );
};
